import {
  trackToolExperience,
  deviceLayout,
} from "../../features/toolExperience/telemetry";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Pin, PinOff, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { toolDefinitions, searchTools } from "../../features/tools/registry";
import { usePinnedTools } from "../../features/tools/usePinnedTools";
import { useToolScope } from "../../features/toolExperience/useToolScope";
import { ToolPage, ToolHeader, ToolToolbar } from "../../components/ToolPage";
import styles from "./ToolsPage.module.css";
export default function ToolsPage() {
  const { user } = useAuth(),
    scope = useToolScope(),
    [q, setQ] = useState("");
  const pins = usePinnedTools(scope, user?.role);
  const results = searchTools(q, user?.role);
  return (
    <ToolPage>
      <ToolHeader
        title="All tools"
        description="Choose a tool for your task. Pin up to six for quick access."
      />
      <ToolToolbar
        search={{ value: q, onChange: setQ, label: "Search tools" }}
      />
      {(
        ["Plan and create", "Audit and compare", "Track and measure"] as const
      ).map((group) => {
        const tools = results.filter((t) => t.group === group);
        return (
          tools.length > 0 && (
            <section key={group} className={styles.group}>
              <h2>{group}</h2>
              <ul>
                {tools.map((tool) => (
                  <li key={tool.id}>
                    <tool.icon size={22} aria-hidden="true" />
                    <Link
                      to={tool.path}
                      onClick={() =>
                        trackToolExperience("tool_discovered", {
                          toolId: tool.id,
                          source: "catalog",
                          layout: deviceLayout(),
                        })
                      }
                    >
                      <strong>{tool.label}</strong>
                      <span>{tool.description}</span>
                    </Link>
                    <button
                      type="button"
                      aria-label={`${pins.ids.includes(tool.id) ? "Unpin" : "Pin"} ${tool.label}`}
                      aria-pressed={pins.ids.includes(tool.id)}
                      disabled={
                        !pins.ids.includes(tool.id) && pins.ids.length >= 6
                      }
                      title={
                        pins.ids.length >= 6 && !pins.ids.includes(tool.id)
                          ? "Unpin another tool first"
                          : undefined
                      }
                      onClick={() =>
                        pins.setPinned(tool.id, !pins.ids.includes(tool.id))
                      }
                    >
                      {pins.ids.includes(tool.id) ? (
                        <PinOff size={18} />
                      ) : (
                        <Pin size={18} />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )
        );
      })}
      {!results.length && <p>No tools match this search.</p>}
      <details className={styles.settings}>
        <summary>Manage pinned tools ({pins.ids.length}/6)</summary>
        <p>Saved for this workspace in this browser.</p>
        <ol>
          {pins.ids.map((id, i) => (
            <li key={id}>
              <span>{toolDefinitions.find((t) => t.id === id)?.label}</span>
              <button
                type="button"
                aria-label={`Move ${id} up`}
                disabled={i === 0}
                onClick={() => pins.movePin(id, "up")}
              >
                <ArrowUp size={18} />
              </button>
              <button
                type="button"
                aria-label={`Move ${id} down`}
                disabled={i === pins.ids.length - 1}
                onClick={() => pins.movePin(id, "down")}
              >
                <ArrowDown size={18} />
              </button>
            </li>
          ))}
        </ol>
        <button type="button" onClick={pins.reset}>
          Restore default pins
        </button>
      </details>
      {toolDefinitions.some(
        (t) =>
          t.availability === "coming-soon" &&
          (!q || t.label.toLowerCase().includes(q.toLowerCase())),
      ) && (
        <section className={styles.group}>
          <h2>Coming soon</h2>
          {toolDefinitions
            .filter(
              (t) =>
                t.availability === "coming-soon" &&
                (!q || t.label.toLowerCase().includes(q.toLowerCase())),
            )
            .map((t) => (
              <p key={t.id}>{t.label}</p>
            ))}
        </section>
      )}
    </ToolPage>
  );
}
